import { PreviewMeetingCubit } from '@breakoutlearning/firebase-repository/cubits/PreviewMeetingCubit'
import { Spinner } from 'components/Spinner'
import { useRepository } from 'hooks/auth'
import { useCubitBuilder } from 'hooks/cubits'
import { MeetingContextProvider } from 'hooks/meeting'
import { useObjectUrlCacheManager } from 'hooks/objectUrl'
import { useBreakoutUser } from 'hooks/profile'
import { Suspense, lazy, useEffect, useMemo } from 'react'

const MeetingPage = lazy(() => import('./MeetingPage'))

export function MeetingPreviewLoader({
  slideDeckId,
  sectionId,
  assignmentId,
}: {
  slideDeckId: string
  /** Students must provide these for API auth */
  sectionId?: string
  assignmentId?: string
}) {
  const currentUser = useBreakoutUser()
  const repository = useRepository()

  const meeting = useCubitBuilder(() => {
    const meeting = new PreviewMeetingCubit({
      currentUser,
      repository,
      slideDeckId,
      sectionId,
      assignmentId,
      enableTalkTrack: true,
    })
    meeting.initialize()
    return meeting
  }, [currentUser, repository, slideDeckId, sectionId, assignmentId])

  const objectUrlCache = useObjectUrlCacheManager()

  const meetingContext = useMemo(() => {
    if (!meeting) return null
    return { meeting: meeting }
  }, [meeting])

  useEffect(() => {
    return () => {
      objectUrlCache.clear('meeting')
    }
  }, [objectUrlCache])

  if (!meetingContext)
    return (
      <div className="absolute box-border h-full w-full">
        <div className="flex h-full w-full items-center justify-center">
          <Spinner reason="meeting_preview_context_loading" />
        </div>
      </div>
    )

  const isReady = meetingContext

  return (
    <div className="box-border h-full w-full bg-core-secondary">
      {!isReady && (
        <div className="flex h-full w-full items-center justify-center">
          <Spinner reason="meeting_preview_not_ready" />
        </div>
      )}

      {isReady && (
        <MeetingContextProvider value={meetingContext}>
          <Suspense
            fallback={<Spinner reason="meeting_preview_page_suspense" />}
          >
            <MeetingPage />
          </Suspense>
        </MeetingContextProvider>
      )}
    </div>
  )
}
